import React, { FC } from 'react'
import classNamesBind from 'classnames/bind'
import Link from 'next/link'
import Router from 'next/router'

import Card from '@dashboard/components/Card'
import CardHeader from '@dashboard/components/CardHeader'
import Button from '@dashboard/components/Button'
import Flex from '@dashboard/components/Flex'
import TypeRank from '@dashboard/components/EnneagramTypeRank'

import { Profile } from '@dashboard/lib/types'
import {
  getTitle,
  getDescription,
  getDescriptionGeneric,
} from '@dashboard/lib/enneagram'

import css from './styles.module.scss'
const classNames = classNamesBind.bind(css)

interface OwnProps {
  profile: Profile
  generic?: boolean
  rankingStyle?: boolean
  emptyStateCta?: {
    text: string
    onClick: () => void
    showSampleLink?: boolean
  }
}

const EnneagramModule: FC<OwnProps> = ({
  profile,
  generic,
  rankingStyle,
  emptyStateCta,
}: OwnProps) => {
  const enneagramType = profile.enneagram_text_type || ''
  const discType = profile?.personality?.disc_type || 'empty'

  // Functions
  const getSampleTypeScores = (): string[] => {
    return ['L', 'I', 'U', 'S', 'T', 'F', 'L', 'P', 'Z']
  }

  const handleOnclick = () => {
    if (emptyStateCta) emptyStateCta.onClick
    else Router.push('/app/register?public_assessment')
  }

  const renderRankingStyle = () => {
    const enneagramType = profile?.enneagram_text_type || ''

    return (
      <Card noPadding>
        <CardHeader icon={'graph'} title="Enneagram" discType="empty" />
        <div className={classNames('testwrapper', 'stacked')}>
          <img
            className={classNames('image', 'small')}
            src={`/static/share/images/enneagram_${enneagramType}.svg`}
          />
          <div className={css.center}>
            <div className={css.subheader}>Enneagram Type</div>
            <div className={classNames('header', 'large')}>
              The {getTitle(enneagramType)} - {enneagramType}
            </div>
          </div>
        </div>
        <div>
          <div className={classNames('rankings-header')}>
            <span>Type Ranking</span>
          </div>
          <div className={classNames('type-rankings', 'pull')}>
            {renderTypeRank(enneagramType, 1)}
          </div>
          <div className={classNames('wrapper')}>
            <div className={classNames('paywalled')}>
              <div className={classNames('type-rankings', 'padded')}>
                {getSampleTypeScores().map((score, index) => {
                  return renderTypeRank(score, index + 1)
                })}
              </div>
            </div>
            <div className={classNames('paywall')}>
              <Flex>
                <Flex vertical alignCenter>
                  <Button onClick={handleOnclick} noMargin>
                    {emptyStateCta?.text || 'See Full Profile'}
                  </Button>
                  {renderSampleLink()}
                </Flex>
              </Flex>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  const renderTypeRank = (type: string, index: number) => {
    return <TypeRank profile={profile} rank={index} type={type} useBasicColor />
  }

  const renderEmptyState = (emptyStateCta: {
    text: string
    onClick: () => void
  }) => {
    const { text, onClick } = emptyStateCta

    return (
      <Card noPadding>
        <CardHeader icon={'graph'} title="Enneagram" discType="empty" />
        <div className={css.testwrapper}>
          <img
            className={css.image}
            src="/static/share/images/enneagram_empty.svg"
          />
          <div className={css.marginTop}>
            <div className={css.header}>Find our your Enneagram Type</div>
            <div className={css.info}>
              Answer a few additional questions to learn more about your
              Enneagram Type.
            </div>
            <Flex>
              <Flex vertical alignCenter>
                <Button onClick={onClick} noMargin>
                  {text}
                </Button>
                {renderSampleLink()}
              </Flex>
            </Flex>
          </div>
        </div>
      </Card>
    )
  }

  const renderSampleLink = () => {
    if (!emptyStateCta?.showSampleLink) return null

    return (
      <Link
        href="/p/bill-gates?show_as_own=true"
        target="_blank"
        rel="noopener noreferrer"
        className={classNames('sampleLink')}
        passHref
      >
        View sample
      </Link>
    )
  }

  if (rankingStyle && profile?.enneagram_text_type) return renderRankingStyle()

  if (emptyStateCta) return renderEmptyState(emptyStateCta)

  if (!profile || !profile.enneagram_text_type) return null

  return (
    <Card noPadding>
      <CardHeader icon={'graph'} title="Enneagram" discType={discType} />
      <div className={css.testwrapper}>
        <img
          className={css.image}
          src={`/static/share/images/enneagram_${enneagramType}.svg`}
        />
        <div className={css.marginTop}>
          <div className={css.subheader}>Enneagram Type</div>
          <div className={classNames('header', `${discType}`)}>
            {enneagramType}: The {getTitle(enneagramType)}
          </div>
          <div className={css.info}>
            {generic
              ? getDescriptionGeneric(enneagramType)
              : getDescription(enneagramType, profile)}
          </div>
          <Button noMargin href={`/app/register?profile_id=${profile.id}`}>
            Sign Up Free
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default EnneagramModule
